import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  trackCompleteRegistration,
  trackPageView,
} from "../utilities/fbpixel";

/**
 * Headless component (renders nothing) mounted once inside <Router>.
 *
 * 1. Fires a Meta Pixel PageView on every client-side route change. The very
 *    first PageView is already fired by the base snippet in index.html, so we
 *    skip the initial render to avoid double-counting.
 *
 * 2. Handles the OAuth signup conversion. Google/Apple signups complete with a
 *    server-side redirect to `/cases?new_user=1` (the server only appends this
 *    flag when it actually created a new account — see authRouter.ts). On that
 *    full page load we fire CompleteRegistration once, then strip the param so a
 *    refresh or back-nav can't re-fire it.
 */
function PixelRouteTracker() {
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialRender = useRef(true);

  // SPA PageView on route changes.
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    trackPageView();
  }, [location.pathname]);

  // OAuth (Google/Apple) signup conversion — fires once on the redirect landing.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("new_user") === "1") {
      trackCompleteRegistration();
      params.delete("new_user");
      const search = params.toString();
      navigate(
        `${window.location.pathname}${search ? `?${search}` : ""}`,
        { replace: true }
      );
    }
    // Intentionally run only on mount: the OAuth redirect is a full page load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

export default PixelRouteTracker;
